/**
 * Renders children into a separate top-level window opened by this renderer.
 * The child window shares this JS context (login, socket, unlocked keys); it
 * only borrows a document, so styles and theme attributes are mirrored in.
 */

import { EnvironmentProvider } from '@ark-ui/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Frame-name prefix the desktop main process allows to open as a child window. */
export const POPOUT_FRAME_PREFIX = 'adieuu-thread-popout:';

const POPOUT_FEATURES = 'popup=yes,width=480,height=720';
const CLOSED_POLL_MS = 500;

function mirrorAttributes(from: Element, to: Element): void {
  for (const attr of [...to.attributes]) {
    if (!from.hasAttribute(attr.name)) to.removeAttribute(attr.name);
  }
  for (const attr of [...from.attributes]) {
    if (to.getAttribute(attr.name) !== attr.value) to.setAttribute(attr.name, attr.value);
  }
}

function cloneStyleNode(node: Node, doc: Document): Node | null {
  if (node instanceof HTMLStyleElement) {
    const style = doc.createElement('style');
    style.textContent = node.textContent;
    for (const attr of [...node.attributes]) style.setAttribute(attr.name, attr.value);
    return style;
  }
  if (node instanceof HTMLLinkElement && node.rel === 'stylesheet') {
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    // Absolute: the popup's about:blank document must not resolve relative to itself.
    link.href = node.href;
    if (node.media) link.media = node.media;
    if (node.crossOrigin != null) link.crossOrigin = node.crossOrigin;
    return link;
  }
  return null;
}

/**
 * Keeps the popup's head stylesheets and `<html>` attributes (theme) in step
 * with the opener. `<body>` is left alone: it carries the opener's layout
 * classes and modal scroll/pointer locks, which must not leak into the popup.
 */
function mirrorDocument(source: Document, target: Document): () => void {
  const clones = new Map<Node, Node>();
  const addStyle = (node: Node) => {
    if (clones.has(node)) return;
    const clone = cloneStyleNode(node, target);
    if (!clone) return;
    clones.set(node, clone);
    target.head.appendChild(clone);
  };
  for (const node of source.head.childNodes) addStyle(node);

  mirrorAttributes(source.documentElement, target.documentElement);

  const headObserver = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'characterData' || (record.type === 'childList' && record.target !== source.head)) {
        const owner = record.target instanceof HTMLStyleElement ? record.target : record.target.parentNode;
        const clone = owner ? clones.get(owner) : undefined;
        if (owner instanceof HTMLStyleElement && clone) clone.textContent = owner.textContent;
        continue;
      }
      for (const node of record.addedNodes) addStyle(node);
      for (const node of record.removedNodes) {
        const clone = clones.get(node);
        if (clone) {
          clone.parentNode?.removeChild(clone);
          clones.delete(node);
        }
      }
    }
  });
  headObserver.observe(source.head, { childList: true, subtree: true, characterData: true });

  const rootObserver = new MutationObserver(() => {
    mirrorAttributes(source.documentElement, target.documentElement);
  });
  rootObserver.observe(source.documentElement, { attributes: true });

  return () => {
    headObserver.disconnect();
    rootObserver.disconnect();
  };
}

export interface PopoutWindowProps {
  /** Stable id; reopening the same id focuses the existing window. */
  id: string;
  title: string;
  /** The user closed the window (or it could not be opened). */
  onClosed: () => void;
  /** Called once if the window could not be opened (popup blocked). */
  onBlocked?: () => void;
  /** The window is open and mounted (e.g. to focus it later). */
  onOpened?: (win: Window) => void;
  children: ReactNode;
}

export function PopoutWindow({ id, title, onClosed, onBlocked, onOpened, children }: PopoutWindowProps) {
  const [mount, setMount] = useState<{ win: Window; container: HTMLElement } | null>(null);
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  const onBlockedRef = useRef(onBlocked);
  onBlockedRef.current = onBlocked;
  const onOpenedRef = useRef(onOpened);
  onOpenedRef.current = onOpened;

  useEffect(() => {
    const win = window.open('about:blank', `${POPOUT_FRAME_PREFIX}${id}`, POPOUT_FEATURES);
    if (!win) {
      onBlockedRef.current?.();
      onClosedRef.current();
      return;
    }
    const doc = win.document;
    doc.open();
    doc.write('<!doctype html><html><head><meta charset="utf-8"></head><body></body></html>');
    doc.close();
    const stopMirroring = mirrorDocument(document, doc);
    const container = doc.createElement('div');
    container.className = 'popout-window-root';
    doc.body.appendChild(container);
    setMount({ win, container });

    let closedByUs = false;
    const reportClosed = () => {
      if (closedByUs) return;
      closedByUs = true;
      onClosedRef.current();
    };
    win.addEventListener('pagehide', reportClosed);
    const poll = window.setInterval(() => {
      if (win.closed) reportClosed();
    }, CLOSED_POLL_MS);
    const closeWithOpener = () => win.close();
    window.addEventListener('pagehide', closeWithOpener);

    return () => {
      closedByUs = true;
      window.clearInterval(poll);
      window.removeEventListener('pagehide', closeWithOpener);
      win.removeEventListener('pagehide', reportClosed);
      stopMirroring();
      setMount(null);
      if (!win.closed) win.close();
    };
  }, [id]);

  useEffect(() => {
    if (mount) mount.win.document.title = title;
  }, [mount, title]);

  useEffect(() => {
    if (!mount) return;
    onOpenedRef.current?.(mount.win);
  }, [mount]);

  if (!mount) return null;
  return createPortal(
    <EnvironmentProvider value={() => mount.win.document}>{children}</EnvironmentProvider>,
    mount.container,
  );
}
